import { create } from 'zustand';

/**
 * A single turn in the AI conversation. Mirrors the wire shape sent to the
 * backend `claimflow/ai/chat` endpoint.
 */
export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Drops a trailing assistant turn that never received a delta — the empty
 * placeholder `startAssistantMessage` pushes while waiting for the first
 * chunk. Aborting a stream must not leave that grey stub behind.
 *
 * Returns the *same* array reference when there is nothing to drop so store
 * subscribers do not re-render on a no-op abort.
 */
function dropTrailingEmptyAssistant(messages: ChatMessage[]): ChatMessage[] {
  const last = messages[messages.length - 1];
  if (last && last.role === 'assistant' && last.content.length === 0) {
    return messages.slice(0, -1);
  }
  return messages;
}

interface AiChatState {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;

  /**
   * The conversation for the claim currently on screen, kept in-memory so
   * closing/reopening the drawer preserves the thread.
   *
   * This store is a module-level singleton shared by every claim, so it holds
   * no claim dimension of its own: the page owns the lifecycle and calls
   * `reset()` whenever the `(ses, idSin)` identity changes. A full page reload
   * clears it too (ephemeral by design).
   */
  messages: ChatMessage[];
  isStreaming: boolean;

  /**
   * Controller for the in-flight `streamChat` request. It lives in the store
   * — not in an `AIChat` ref — so `reset()` can cancel the request itself.
   * A params-only `/task/A` -> `/task/B` navigation never unmounts `AIChat`,
   * so a component-local ref could not be relied on to stop claim A's stream
   * from writing its tokens into claim B's freshly-cleared thread.
   *
   * Plumbing, not render state — do not select it in a component.
   */
  abortController: AbortController | null;
  setAbortController: (controller: AbortController | null) => void;

  appendUserMessage: (content: string) => void;
  /** Push an empty assistant turn and flip `isStreaming` on. */
  startAssistantMessage: () => void;
  /** Append a streamed chunk to the last assistant turn. */
  appendAssistantDelta: (text: string) => void;
  /** Mark the stream as finished. */
  finishAssistant: () => void;
  /**
   * Cancel any in-flight request and stop streaming, keeping the transcript.
   * Used when the drawer closes or `AIChat` unmounts.
   */
  abortStream: () => void;
  /**
   * Cancel in-flight work and clear the panel back to its first-open state.
   * Called by `TaskDetailPage` on every `(ses, idSin)` change so entering a
   * claim always yields an empty thread — no transcript from another claim
   * survives on screen or in the history sent to the LLM.
   */
  reset: () => void;
}

export const useAiChatStore = create<AiChatState>((set, get) => ({
  open: false,
  setOpen: (open) => {
    set({ open });
  },
  toggle: () => {
    set((s) => ({ open: !s.open }));
  },

  messages: [],
  isStreaming: false,
  abortController: null,

  setAbortController: (controller) => {
    set({ abortController: controller });
  },

  appendUserMessage: (content) => {
    set((s) => ({ messages: [...s.messages, { role: 'user', content }] }));
  },

  startAssistantMessage: () => {
    set((s) => ({
      messages: [...s.messages, { role: 'assistant', content: '' }],
      isStreaming: true,
    }));
  },

  appendAssistantDelta: (text) => {
    set((s) => {
      const messages = s.messages.slice();
      const lastIndex = messages.length - 1;
      const last = messages[lastIndex];
      if (last && last.role === 'assistant') {
        messages[lastIndex] = { ...last, content: last.content + text };
      }
      return { messages };
    });
  },

  finishAssistant: () => {
    set({ isStreaming: false });
  },

  abortStream: () => {
    const { abortController, messages } = get();
    abortController?.abort();
    set({
      abortController: null,
      isStreaming: false,
      messages: dropTrailingEmptyAssistant(messages),
    });
  },

  reset: () => {
    get().abortController?.abort();
    set({
      abortController: null,
      messages: [],
      isStreaming: false,
      open: false,
    });
  },
}));
